// footer/ToolbarOverflow.tsx
// 自适应折叠工具栏（对齐千问 footer「更多」行为）：宽度充足时按钮平铺；空间不足时，
// 从右往左把放不下的按钮收进「更多」下拉。收进下拉的仍是**原按钮组件本身**（非复制）——
// 智能体菜单挂 body、技能面板锚定 .rich-text-editor、AI生图纯 onClick，三者的弹层定位
// 都不依赖按钮在内联区还是下拉里，故按钮移入下拉后行为不变。
//
// 「选完就关」：技能面板 / 智能体二级下拉由按钮组件自身渲染，若点一下就立刻关闭「更多」，
// 会连按钮带弹层一起卸载（面板还没用就没了）。故这里把「更多」做成受控开合 + 白名单式
// 外部点击：浏览子弹层时保持打开，直到 item 通过 close()（真正选中）或点击「更多」按钮/
// 空白处才收起。item 的 node 可写成函数拿到 { close } 以在选中回调里主动收起。
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'antd';
import { rteSelectPopupContainer } from '../utils/selectPlacements';

export type ToolItemRenderCtx = {
  close: () => void;
  /** 该节点当前是否渲染在「更多」菜单里（true）还是内联工具栏（false）。
   *  用于按位置切换行为，如智能体的二级菜单在菜单里改从右侧展开。 */
  inMenu: boolean;
};
/** 「更多」菜单里代理行的上下文：close 收起菜单；activate 触发常驻内联的真实按钮（点它的根元素） */
export type ToolItemMenuCtx = { close: () => void; activate: () => void };

export type ToolItem = {
  /** 稳定 key：宽度缓存、内联/折叠切换的身份依据 */
  key: string;
  /** 直接给节点，或给渲染函数以拿到 close（在「选中」回调里主动收起「更多」） */
  node: React.ReactNode | ((ctx: ToolItemRenderCtx) => React.ReactNode);
  /**
   * 可选：该按钮的弹层锚定编辑器 / 由按钮自身渲染（如技能面板），不能随「更多」菜单卸载。
   * 提供后：真实按钮**常驻内联**（溢出时 CSS 隐藏但保持挂载，弹层定位/生命周期不受影响），
   * 「更多」菜单里改渲染此代理行；代理行 activate() 会触发内联真实按钮。
   * 不提供则走默认：溢出时真实按钮整体移入「更多」菜单。
   */
  renderMenuItem?: (ctx: ToolItemMenuCtx) => React.ReactNode;
  /** 置顶常驻：永不折叠进「更多」，且排到最前（对齐千问——选中某工具后把它「拿出来」常驻工具栏）。 */
  pinned?: boolean;
};

type Props = {
  items: ToolItem[];
  /** 内容可能改变某个按钮宽度时变化（如选中技能后显示技能名）→ 清缓存重新测量 */
  measureKey?: string;
  disabled?: boolean;
};

// 项与项之间、以及末项与「更多」之间的间距（与下方 CSS .rte-toolbar-overflow gap 保持一致）
const GAP = 8;
// 「更多」按钮宽度兜底：首帧尚未渲染出「更多」时用它预留，渲染后以实测值为准
const MORE_RESERVE = 72;
// 离场（吞进「更多」）弹出动画时长，需与 CSS .rte-toolbar-item--leaving 一致，播完再卸载
const EXIT_MS = 100;
// 点击这些子弹层内部时，不收起「更多」（它们是折叠按钮自身弹出的二级面板，挂在别处）
const CHILD_POPUP_SELECTOR =
  '.rte-more-menu,[data-rte-more-button],.rte-skill-panel-popup,.rte-skill-delete-confirm,.rte-skill-share-popover,.rte-agent-dropdown,.ant-dropdown';

// 「更多」图标：设计稿 figma node 2901:54496 的宫格矢量（三方块 + 右上旋转方块）。
// fill 用 currentColor 跟随文字色，hover / 主题色自动变化，无需多份 SVG。
const MoreIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.58984 9.14355C5.95404 9.14355 6.304 9.28838 6.56152 9.5459C6.81867 9.80323 6.96372 10.1527 6.96387 10.5166V14.1943C6.96373 14.5583 6.81895 14.9086 6.56152 15.166C6.30405 15.4233 5.9539 15.5684 5.58984 15.5684H1.91309C1.54903 15.5684 1.19888 15.4233 0.941406 15.166C0.683978 14.9086 0.539199 14.5583 0.539062 14.1943V10.5176C0.539067 10.1535 0.684113 9.80337 0.941406 9.5459C1.19894 9.28837 1.54889 9.14355 1.91309 9.14355H5.58984ZM2.28027 10.4199C2.15705 10.42 2.03931 10.4687 1.95215 10.5557C1.86493 10.643 1.81545 10.7616 1.81543 10.8848V13.8271L1.82422 13.918C1.84226 14.0073 1.88675 14.0899 1.95215 14.1553C2.03954 14.2425 2.15733 14.2919 2.28027 14.292H5.22266L5.31348 14.2832C5.34338 14.2772 5.37314 14.2685 5.40137 14.2568C5.45705 14.2337 5.50752 14.1985 5.55078 14.1553C5.59381 14.1122 5.62895 14.0611 5.65234 14.0049C5.66405 13.9766 5.67271 13.9469 5.67871 13.917L5.6875 13.8271V10.8848C5.68748 10.7616 5.638 10.643 5.55078 10.5557C5.48537 10.4904 5.40283 10.4465 5.31348 10.4287L5.22266 10.4199H2.28027Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.9453 9.03711C13.3093 9.03724 13.6586 9.18214 13.916 9.43945C14.1734 9.69694 14.3184 10.047 14.3184 10.4111V14.0879C14.3183 14.268 14.2817 14.447 14.2129 14.6133C14.1439 14.7796 14.0435 14.9321 13.916 15.0596C13.7885 15.187 13.6361 15.2875 13.4697 15.3564C13.345 15.4081 13.2133 15.4417 13.0791 15.4551L12.9443 15.4619H9.26758C8.90349 15.4619 8.55339 15.3169 8.2959 15.0596C8.03855 14.8022 7.89369 14.4518 7.89355 14.0879V10.4111C7.89355 10.0471 8.03861 9.69693 8.2959 9.43945C8.55342 9.18193 8.90337 9.03711 9.26758 9.03711H12.9453ZM9.63477 10.3135C9.5741 10.3135 9.51355 10.3253 9.45703 10.3486C9.40075 10.372 9.34976 10.4071 9.30664 10.4502C9.26346 10.4935 9.22825 10.5439 9.20508 10.5996C9.18175 10.6561 9.16996 10.7176 9.16992 10.7783V13.7207L9.17871 13.8115C9.18467 13.8413 9.19353 13.8704 9.20508 13.8984C9.22833 13.9543 9.26342 14.0056 9.30664 14.0488C9.3498 14.0919 9.40085 14.1271 9.45703 14.1504C9.51353 14.1737 9.57417 14.1855 9.63477 14.1855H12.5771L12.668 14.1768C12.7576 14.1588 12.8407 14.1143 12.9062 14.0488C12.9714 13.9835 13.0154 13.9009 13.0332 13.8115L13.042 13.7207V10.7783C13.042 10.7169 13.0309 10.6555 13.0078 10.5996C12.9847 10.544 12.9494 10.4935 12.9062 10.4502C12.8632 10.4072 12.8121 10.372 12.7559 10.3486C12.7276 10.3369 12.6978 10.3283 12.668 10.3223L12.5771 10.3135H9.63477Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.1064 0.919922C11.2869 0.919986 11.4649 0.957675 11.6309 1.02637C11.7973 1.09534 11.9497 1.19581 12.0771 1.32324L14.6777 3.92285C14.8051 4.05027 14.9057 4.20287 14.9746 4.36914C15.0434 4.53542 15.0801 4.71436 15.0801 4.89453L15.0732 5.0293C15.0599 5.16344 15.0263 5.29518 14.9746 5.41992C14.9056 5.58618 14.8052 5.73878 14.6777 5.86621L12.0781 8.46582C11.9508 8.59306 11.7984 8.69464 11.6318 8.76367C11.4652 8.83262 11.2864 8.86815 11.1064 8.86816C10.9264 8.86816 10.7477 8.83263 10.5811 8.76367C10.4144 8.69463 10.2621 8.59311 10.1348 8.46582L7.53516 5.86621C7.40782 5.73889 7.30637 5.58648 7.2373 5.41992C7.18567 5.29512 7.15273 5.1633 7.13965 5.0293L7.13281 4.89453C7.13288 4.71481 7.16857 4.5364 7.2373 4.37012C7.30634 4.20346 7.40786 4.05118 7.53516 3.92383L10.1348 1.32324C10.2623 1.19577 10.4146 1.09533 10.5811 1.02637C10.7472 0.95759 10.9259 0.919922 11.1064 0.919922ZM11.1064 2.34863C10.9829 2.34863 10.8645 2.39735 10.7773 2.48438L8.69727 4.56543C8.6541 4.60867 8.61889 4.65915 8.5957 4.71484C8.57239 4.77125 8.5606 4.83282 8.56055 4.89355L8.56934 4.98438C8.57533 5.0143 8.58403 5.04407 8.5957 5.07227C8.61912 5.12852 8.65424 5.17964 8.69727 5.22266L10.7764 7.30176L10.8486 7.36035L10.9082 7.39453C10.9699 7.42383 11.0376 7.43945 11.1064 7.43945L11.1973 7.43066C11.2864 7.41262 11.369 7.36809 11.4346 7.30273L13.5156 5.22266C13.5586 5.17966 13.5938 5.12836 13.6172 5.07227C13.6402 5.01649 13.6513 4.95494 13.6514 4.89355L13.6426 4.80273C13.6368 4.77268 13.6288 4.74287 13.6172 4.71484C13.5941 4.65927 13.5588 4.60872 13.5156 4.56543L11.4346 2.48438C11.3692 2.41913 11.2866 2.37521 11.1973 2.35742L11.1064 2.34863Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.58984 1.68262C5.95404 1.68262 6.304 1.82744 6.56152 2.08496C6.81867 2.34229 6.96372 2.69177 6.96387 3.05566V6.7334C6.96373 7.09738 6.81895 7.44765 6.56152 7.70508C6.30405 7.96237 5.9539 8.10742 5.58984 8.10742H1.91309C1.54903 8.10742 1.19888 7.96236 0.941406 7.70508C0.683978 7.44765 0.539199 7.09738 0.539062 6.7334V3.05664C0.539067 2.69259 0.684113 2.34244 0.941406 2.08496C1.19894 1.82744 1.54889 1.68262 1.91309 1.68262H5.58984ZM2.28027 2.95898C2.15705 2.95905 2.03931 3.00778 1.95215 3.09473C1.86493 3.18207 1.81545 3.30065 1.81543 3.42383V6.36621L1.82422 6.45703C1.84226 6.54634 1.88675 6.62894 1.95215 6.69434C2.03954 6.78154 2.15733 6.83099 2.28027 6.83105H5.22266L5.31348 6.82227C5.34338 6.81627 5.37314 6.80758 5.40137 6.7959C5.45705 6.77272 5.50752 6.73752 5.55078 6.69434C5.59381 6.65131 5.62895 6.60017 5.65234 6.54395C5.66405 6.51566 5.67271 6.48593 5.67871 6.45605L5.6875 6.36621V3.42383C5.68748 3.30065 5.638 3.18207 5.55078 3.09473C5.48537 3.02947 5.40283 2.98556 5.31348 2.96777L5.22266 2.95898H2.28027Z"
      fill="currentColor"
    />
  </svg>
);

/** 取元素含左右 margin 的外框宽度（按钮自身可能带 margin-left，测量需计入）。
 *  用 offsetWidth 而非 getBoundingClientRect().width：后者会被进场 pop-in 的 transform:scale
 *  影响，动画中量到的是缩小后的宽度，导致判溢出时误以为「还放得下」而多留一个按钮，
 *  空间紧张（如容器带 margin）时就会顶出内容边缘被 overflow:hidden 裁掉而非收进「更多」。 */
function outerWidth(el: HTMLElement): number {
  const cs = getComputedStyle(el);
  return el.offsetWidth + (parseFloat(cs.marginLeft) || 0) + (parseFloat(cs.marginRight) || 0);
}

export const ToolbarOverflow: React.FC<Props> = ({ items, measureKey, disabled }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  // key → 内联时实测的外框宽度。折叠进「更多」的项读上次缓存值（宽度是固有的、与容器无关）
  const widthCache = useRef(new Map<string, number>());
  const itemRefs = useRef(new Map<string, HTMLElement>());
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [moreOpen, setMoreOpen] = useState(false);
  // 容器宽度变化时自增，驱动下方测量 effect 重算
  const [sizeVersion, setSizeVersion] = useState(0);
  // 正在「离场」（被吞进「更多」）的 key：先原地播弹出动画，延迟卸载。
  const [leaving, setLeaving] = useState<Set<string>>(() => new Set());
  const prevShownRef = useRef<Set<string>>(new Set());
  const leaveTimers = useRef<Map<string, number>>(new Map());

  // 排序：pinned 项提到最前且永不折叠（对齐千问——选中的工具被「拿出来」常驻工具栏）。
  const ordered = useMemo(
    () => [...items.filter((it) => it.pinned), ...items.filter((it) => !it.pinned)],
    [items],
  );
  const pinnedCount = useMemo(() => ordered.filter((it) => it.pinned).length, [ordered]);

  const closeMore = () => setMoreOpen(false);
  const renderNode = (it: ToolItem, inMenu: boolean): React.ReactNode =>
    typeof it.node === 'function' ? it.node({ close: closeMore, inMenu }) : it.node;
  // 触发常驻内联的真实按钮：点其容器内第一个元素（即按钮根，如技能 badge 的 onClick）
  const activateInline = (key: string) => {
    const el = itemRefs.current.get(key)?.firstElementChild as HTMLElement | undefined;
    el?.click();
  };

  // measureKey 变化：按钮内容宽度可能变 → 清缓存并全部内联，让测量 effect 重新量一遍。
  // 全部内联只是 layout 阶段的过渡态，useLayoutEffect 会在 paint 前收敛，无可见闪烁。
  useLayoutEffect(() => {
    widthCache.current.clear();
    setVisibleCount(items.length);
  }, [measureKey, items.length]);

  // 监听容器可用宽度变化
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setSizeVersion((v) => v + 1));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 「更多」打开时的外部点击收起：命中「更多」按钮/菜单或任一折叠按钮的子弹层则忽略，
  // 其余点击才收起。子弹层内的选中由 item 的 close() 负责收起（见 Footer 里各选中回调）。
  useEffect(() => {
    if (!moreOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Element | null;
      if (t && t.closest(CHILD_POPUP_SELECTOR)) return;
      setMoreOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [moreOpen]);

  // 测量 + 计算可见数量。放在 layout 阶段：setState 触发的重排在浏览器 paint 前完成，
  // 即便首帧全内联溢出，用户也看不到中间态。
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // 刷新当前内联项的宽度缓存
    for (let i = 0; i < visibleCount && i < ordered.length; i++) {
      const el = itemRefs.current.get(ordered[i].key);
      if (el) widthCache.current.set(ordered[i].key, outerWidth(el));
    }

    const available = container.clientWidth;
    const moreW = moreRef.current ? outerWidth(moreRef.current) : MORE_RESERVE;
    const widths = ordered.map((it) => widthCache.current.get(it.key) ?? MORE_RESERVE);

    // 全部都放得下（且不需要「更多」）→ 全部平铺
    const totalNoMore = widths.reduce((s, w) => s + w, 0) + GAP * Math.max(0, ordered.length - 1);

    let next: number;
    if (totalNoMore <= available) {
      next = ordered.length;
    } else {
      // 需要「更多」：预留其宽度，从左往右尽量塞
      let used = 0;
      let k = 0;
      for (; k < ordered.length; k++) {
        const w = widths[k];
        const gap = k > 0 ? GAP : 0;
        // 末尾始终跟着「更多」按钮（含一个 GAP）
        if (used + gap + w + GAP + moreW > available) break;
        used += gap + w;
      }
      next = k;
    }

    // pinned 项排在最前、永不折叠：即使极窄也至少显示它们（宁可轻微溢出，overflow:hidden 兜底）
    next = Math.max(next, pinnedCount);

    if (next !== visibleCount) setVisibleCount(next);
  }, [ordered, pinnedCount, visibleCount, sizeVersion]);

  // 有置顶项（某工具被激活/拉出）时：只显示置顶项，其余按钮与「更多」全部隐藏
  //（对齐千问——激活某工具后它独占工具栏，需先退出该工具才回到其他按钮）。
  const hasPinned = pinnedCount > 0;
  const overflowItems = hasPinned ? [] : ordered.slice(visibleCount);

  // 当前应内联显示（可见）的 key 集合：置顶模式只有置顶项，否则是可见前缀。
  const shownKeys = useMemo(() => {
    const s = new Set<string>();
    if (hasPinned) ordered.forEach((it) => it.pinned && s.add(it.key));
    else ordered.slice(0, visibleCount).forEach((it) => s.add(it.key));
    return s;
  }, [hasPinned, ordered, visibleCount]);
  const shownSig = [...shownKeys].join('|');

  // 可见集合变化时：离开可见区的 key 进入「离场」播弹出动画，EXIT_MS 后真正卸载/隐藏；
  // 若期间又回到可见区则取消离场。prefers-reduced-motion 时不延迟、直接消失。
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const timers = leaveTimers.current;
    if (!reduce) {
      prevShownRef.current.forEach((key) => {
        if (!shownKeys.has(key) && !timers.has(key)) {
          setLeaving((s) => new Set(s).add(key));
          const t = window.setTimeout(() => {
            timers.delete(key);
            setLeaving((s) => {
              const n = new Set(s);
              n.delete(key);
              return n;
            });
          }, EXIT_MS);
          timers.set(key, t);
        }
      });
    }
    // 重新进入可见区 → 取消离场
    shownKeys.forEach((key) => {
      const t = timers.get(key);
      if (t !== undefined) {
        window.clearTimeout(t);
        timers.delete(key);
        setLeaving((s) => {
          if (!s.has(key)) return s;
          const n = new Set(s);
          n.delete(key);
          return n;
        });
      }
    });
    prevShownRef.current = shownKeys;
    // shownSig 是 shownKeys 的稳定签名；shownKeys 每次新建引用故不入依赖
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownSig]);

  // 卸载时清掉所有未完成的离场定时器
  useEffect(() => () => leaveTimers.current.forEach((t) => window.clearTimeout(t)), []);

  return (
    <div className="rte-toolbar-overflow" ref={containerRef}>
      {ordered.map((it) => {
        const shown = shownKeys.has(it.key);
        // leaving 状态由 effect 在渲染后才置上；prevShownRef 补齐「本帧刚离开」的判断，
        // 避免离场首帧按钮先瞬间消失再重新出现（闪烁）。effect 更新 prevShownRef 后由 leaving 接力。
        const isLeaving = !shown && (leaving.has(it.key) || prevShownRef.current.has(it.key));

        // 既不显示也不在离场：完全隐藏。
        // renderMenuItem 项（技能）常驻内联但 display:none——保住弹层定位与 activateInline 挂载；
        // 普通项直接不渲染（其真身移入「更多」菜单）。
        if (!shown && !isLeaving) {
          if (!it.renderMenuItem) return null;
          return (
            <div
              key={it.key}
              className="rte-toolbar-item"
              style={{ display: 'none' }}
              ref={(el) => {
                if (el) itemRefs.current.set(it.key, el);
                else itemRefs.current.delete(it.key);
              }}
            >
              {renderNode(it, false)}
            </div>
          );
        }

        // 显示或离场中：内联渲染。离场加 --leaving 播弹出（缩小+淡出）动画。
        return (
          <div
            key={it.key}
            className={`rte-toolbar-item${isLeaving ? ' rte-toolbar-item--leaving' : ''}`}
            ref={(el) => {
              if (el) itemRefs.current.set(it.key, el);
              else itemRefs.current.delete(it.key);
            }}
          >
            {renderNode(it, false)}
          </div>
        );
      })}
      {overflowItems.length > 0 && (
        <Dropdown
          open={moreOpen}
          // 受控：仅接受「打开」与「点触发器收起」；外部点击的收起由上面的白名单监听接管，
          // 以免点技能面板/智能体二级下拉（antd 视作外部）时误关「更多」把子面板一起卸载。
          onOpenChange={(nextOpen, info) => {
            if (nextOpen) setMoreOpen(true);
            else if (info?.source === 'trigger') setMoreOpen(false);
          }}
          disabled={disabled}
          trigger={['click']}
          placement="topLeft"
          getPopupContainer={rteSelectPopupContainer}
          popupRender={() => (
            <div className="rte-more-menu">
              {overflowItems.map((it) => (
                <div key={it.key} className="rte-more-menu-item">
                  {it.renderMenuItem
                    ? it.renderMenuItem({ close: closeMore, activate: () => activateInline(it.key) })
                    : renderNode(it, true)}
                </div>
              ))}
            </div>
          )}
        >
          <div
            ref={moreRef}
            className={`rte-more-badge${moreOpen ? ' rte-more-badge-active' : ''}`}
            data-rte-more-button="true"
            style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            <MoreIcon />
            <span>更多</span>
          </div>
        </Dropdown>
      )}
    </div>
  );
};
ToolbarOverflow.displayName = 'ToolbarOverflow';
